import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { DIVISIONI } from "@/data/content";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#1D1D1D] border border-[#A5A5A5]/25 text-[#F5F2EB] placeholder-[#A5A5A5]/50 px-5 py-4 text-sm font-light focus:outline-none focus:border-[#E5C776] focus:ring-1 focus:ring-[#E5C776] transition-colors duration-300";

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="block eyebrow mb-3 !text-[10px]">
      {label}
    </label>
    {children}
  </div>
);

export default function DiventaPartner() {
  const { t } = useLang();
  const [form, setForm] = useState({
    nome: "",
    email: "",
    azienda: "",
    telefono: "",
    divisione: "",
    messaggio: "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/partnership`, {
        ...form,
        azienda: form.azienda || null,
        telefono: form.telefono || null,
        divisione: form.divisione || null,
      });
      toast.success(t("form.successCandidatura"), {
        description: t("form.successDesc"),
      });
      setForm({ nome: "", email: "", azienda: "", telefono: "", divisione: "", messaggio: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(t("form.errorTitle"), {
        description: typeof detail === "string" ? detail : t("form.errorDesc"),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="diventa-partner-page" className="bg-[#080808] min-h-screen">
      <section className="pt-40 pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow={t("partner.eyebrow")} title={t("partner.title")} goldWord={t("partner.gold")} />
            <Reveal delay={0.2}>
              <p className="mt-10 text-lg font-light text-[#A5A5A5] leading-relaxed">
                {t("partner.intro")}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-12 space-y-8">
                <div>
                  <p className="eyebrow mb-2 !text-[10px]">Come funziona</p>
                  <p className="text-sm text-[#F5F2EB] font-light">
                    1. Inviate la candidatura con il vostro profilo
                  </p>
                  <p className="mt-2 text-sm text-[#F5F2EB] font-light">
                    2. Il team Partnership vi contatta per un colloquio riservato
                  </p>
                  <p className="mt-2 text-sm text-[#F5F2EB] font-light">
                    3. Definiamo insieme il percorso di ingresso nel Gruppo
                  </p>
                </div>
                <div className="gold-line w-16 opacity-60" />
                <div>
                  <p className="eyebrow mb-2 !text-[10px]">Riservatezza</p>
                  <p className="text-sm text-[#A5A5A5] font-light">
                    Tutte le informazioni inviate sono trattate con la massima
                    riservatezza e non saranno condivise con terzi.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form
                onSubmit={submit}
                data-testid="partnership-form"
                className="bg-[#141414] border border-[#C6A45D]/20 p-8 sm:p-12 space-y-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Field label={`${t("form.nome")} *`} htmlFor="p-nome">
                    <input
                      id="p-nome"
                      data-testid="partnership-input-nome"
                      required
                      minLength={2}
                      value={form.nome}
                      onChange={set("nome")}
                      placeholder={t("partner.placeholderNome")}
                      className={inputCls}
                    />
                  </Field>
                  <Field label={`${t("form.email")} *`} htmlFor="p-email">
                    <input
                      id="p-email"
                      data-testid="partnership-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set("email")}
                      placeholder="nome@azienda.it"
                      className={inputCls}
                    />
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Field label={t("form.azienda")} htmlFor="p-azienda">
                    <input
                      id="p-azienda"
                      data-testid="partnership-input-azienda"
                      value={form.azienda}
                      onChange={set("azienda")}
                      placeholder="Nome dell'organizzazione"
                      className={inputCls}
                    />
                  </Field>
                  <Field label={t("form.telefono")} htmlFor="p-telefono">
                    <input
                      id="p-telefono"
                      data-testid="partnership-input-telefono"
                      type="tel"
                      value={form.telefono}
                      onChange={set("telefono")}
                      placeholder="+39 ..."
                      className={inputCls}
                    />
                  </Field>
                </div>
                <Field label={t("form.unit")} htmlFor="p-divisione">
                  <select
                    id="p-divisione"
                    data-testid="partnership-select-divisione"
                    value={form.divisione}
                    onChange={set("divisione")}
                    className={`${inputCls} appearance-none cursor-pointer`}
                  >
                    <option value="">{t("form.noPref")}</option>
                    {DIVISIONI.map((d) => (
                      <option key={d.id} value={d.nome}>
                        Billions {d.nome}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={`${t("form.profilo")} *`} htmlFor="p-messaggio">
                  <textarea
                    id="p-messaggio"
                    data-testid="partnership-input-messaggio"
                    required
                    minLength={10}
                    rows={6}
                    value={form.messaggio}
                    onChange={set("messaggio")}
                    placeholder={t("partner.placeholderMsg")}
                    className={`${inputCls} resize-none`}
                  />
                </Field>
                <button
                  type="submit"
                  data-testid="partnership-submit-button"
                  disabled={sending}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 bg-[#C6A45D] text-[#080808] text-xs tracking-[0.25em] uppercase font-semibold hover:bg-[#E5C776] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
                >
                  {sending ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      {t("form.invio")}
                    </>
                  ) : (
                    <>
                      {t("form.inviaCandidatura")}
                      <Send size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
